import { Alert, AlertDescription, Box, Chip } from '@locus-taxy/disco-ui';
import { useClientId } from '../hooks/useClientId';
import { EntityGridViewWrapper } from '@locus-taxy/encore-grid';
import type { CustomAppRenderProps } from '@locus-taxy/encore-grid';
import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../config';
import { useEntityFormAdditionalContext } from '../hooks/useEntityFormAdditionalContext';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';
import { useRouteContext } from '../hooks/useRouteContext';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { isMfeAppConfig } from '../utils/customApp';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import { CustomAppPanel } from '../customApps/CustomAppUnavailable';
import { useEntityMetadata } from '../api/endpoints/useEntityMetadata';
import { useUsecaseLabel } from '../hooks/useUsecaseLabel';
import { useAppRegistry } from '../api/endpoints/useAppRegistry';
import { useActiveNavGroupIcon } from '../hooks/useActiveNavGroupIcon';
import { useOpenDependentGrid } from '../hooks/useOpenDependentGrid';
import LoadingSpinner from './LoadingSpinner';
import {
  BASE_CONDITION_PARAM,
  parseBaseConditionParam,
} from '../utils/baseConditionParam';

interface EntityGridViewProps {
  selectedEntity: string | undefined;
  /**
   * Usecase slug (DP-3570). When set, the grid resolves the `(entity, usecase)`
   * view-config document; a whole-document fallback (the backend resolved a
   * different document than requested) surfaces a non-blocking notice.
   */
  usecase?: string;
}

export default function EntityGridView({
  selectedEntity,
  usecase,
}: Readonly<EntityGridViewProps>) {
  const clientId = useClientId();
  const apiBaseUrl = useMemo(() => {
    if (!clientId) {
      return '';
    }
    return getApiBaseUrl(clientId);
  }, [clientId]);

  // Get user context for form additional context
  const { additionalContext: userContext } = useEntityFormAdditionalContext();

  const routeContext = useRouteContext();
  const { t } = useTranslation();
  // DP-4118: `appConfig.remoteApp` may point a custom app at another app's bundle.
  const {
    remoteAppFor,
    studioFor,
    isReady: registryReady,
  } = useAppRegistry(clientId);

  // The grid draws an icon in the ornament ahead of a workflow action's title
  // and cannot resolve one itself — it has no nav tree — so we hand it this
  // page's parent nav-group icon, the same one the sidebar shows. Undefined
  // when the route sits under no group (or modules are still loading); the grid
  // falls back to its own generic box, so no placeholder is substituted here.
  const NavGroupIcon = useActiveNavGroupIcon();
  const actionTitleIcon = useMemo(
    () => (NavGroupIcon ? <NavGroupIcon /> : undefined),
    [NavGroupIcon]
  );
  // "View in grid" in a single-row action's impact preview (DP-8964).
  const openDependentGrid = useOpenDependentGrid();

  // Whole-document fallback notice (DP-3570 FR-20): the grid renders whatever
  // document the backend resolved; we read the same metadata here to compare
  // the resolved usecase against the one the URL requested. When they differ,
  // the requested usecase document does not exist and the default view is being
  // shown — surface a non-blocking notice. `detailsFellBack` is deliberately
  // NOT surfaced (by-design observability flag, not user-facing).
  //
  // `resolvedUsecase` is serialized NON_NULL, so a fallback resolution OMITS the
  // field rather than sending an explicit null — an absent field IS the fallback
  // signal and must not be read as "no information". Gate on the metadata having
  // arrived instead; a pre-feature backend also omits the field, and there the
  // notice is still correct (the usecase param is ignored, so the default
  // document really is what rendered).
  const { data: entityMetadata } = useEntityMetadata(
    clientId,
    selectedEntity ?? '',
    { usecase }
  );
  /*
   * Header title on a usecase page: show the usecase's own label ("Dispatch") rather than the
   * entity's schema title ("Customer Shipment"). Undefined on the default page, and also on a
   * deep-linked usecase page whose leaf the role cannot see — the grid then keeps the schema title.
   */
  const usecaseLabel = useUsecaseLabel(selectedEntity, usecase);

  // Caller-supplied grid filter (`p.baseCondition`): page-scoped so it never
  // rides the DP-6873 query merge onto an unrelated grid. Malformed → unfiltered.
  const [searchParams, setSearchParams] = useSearchParams();
  const baseConditionValue = searchParams.get(BASE_CONDITION_PARAM);
  const baseCondition = useMemo(
    () => parseBaseConditionParam(baseConditionValue),
    [baseConditionValue]
  );
  // `replace`, so browser Back does not bring the cleared filter back (same as
  // `useDimensionSelection`).
  const clearBaseCondition = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(BASE_CONDITION_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const resolvedUsecase = entityMetadata?.data?.resolvedUsecase;
  const showFallbackNotice = Boolean(
    usecase && entityMetadata && resolvedUsecase !== usecase
  );

  const renderCustomApp = useCallback(
    (props: CustomAppRenderProps) => {
      if (!isMfeAppConfig(props.customApp)) {
        return null;
      }
      if (!registryReady) {
        return <LoadingSpinner />;
      }
      const resolvedConfig = resolveAppConfig(
        props.customApp,
        props.entityName,
        clientId,
        remoteAppFor(props.customApp.appName),
        studioFor(props.customApp.appName)
      );
      if (!resolvedConfig) {
        return null;
      }
      /* Panel-sized failure, not the app-wide one: the default control is
         "Refresh Page" wired to window.location.reload(), which reloads the
         whole dashboard for one failed panel (DP-6140). CustomAppPanel's retry
         re-fetches this app alone. */
      return (
        <CustomAppPanel
          config={resolvedConfig}
          clientId={props.clientId}
          entityName={props.entityName}
          entityTitle={props.customApp.name}
          entityData={props.entityData ?? {}}
        />
      );
    },
    [clientId, registryReady, remoteAppFor, studioFor]
  );

  if (!selectedEntity) {
    return null;
  }

  return (
    <Box className="flex min-h-0 w-full max-w-full flex-grow flex-row overflow-hidden">
      <Box className="flex min-h-0 w-full max-w-full flex-grow flex-col overflow-hidden">
        <Box
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'table',
            entity: selectedEntity,
            page: 'grid',
          })}
          className="flex min-h-0 flex-grow flex-col overflow-hidden"
        >
          {showFallbackNotice && (
            <Alert
              variant="info"
              className="mx-4 mt-2"
              data-testid="usecase-fallback-notice"
            >
              <AlertDescription>
                {t(
                  'dashboard.usecase.fallbackNotice',
                  'This view is not configured yet — showing the default view.'
                )}
              </AlertDescription>
            </Alert>
          )}
          {baseCondition && (
            <Box className="mx-4 mt-2 flex items-center">
              <Chip
                label={t('dashboard.grid.baseCondition.chip', 'Filtered view')}
                variant="subtle"
                color="info"
                size="sm"
                onDismiss={clearBaseCondition}
                dismissLabel={t('dashboard.grid.baseCondition.clear', 'Clear')}
                data-testid={getTestId({
                  action: TestIdAction.VIEW,
                  object: 'base-condition-chip',
                  entity: selectedEntity,
                  page: 'grid',
                })}
              />
            </Box>
          )}
          <Box className="flex min-h-0 flex-grow flex-col overflow-hidden">
            <EntityGridViewWrapper
              clientId={clientId}
              selectedEntity={selectedEntity}
              usecase={usecase}
              baseCondition={baseCondition}
              entityTitle={usecaseLabel}
              apiBaseUrl={apiBaseUrl}
              googleApiKey={GOOGLE_MAPS_API_KEY}
              routeContext={routeContext}
              additionalContext={userContext}
              renderCustomApp={renderCustomApp}
              titleIcon={actionTitleIcon}
              onViewDependents={openDependentGrid}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
